// frontend/src/components/ProtectedRoute.js
// Componente que protege rutas: redirige a /login si no está autenticado o hay sesión cruzada.
// Opcionalmente restringe por rol (prop 'rolesPermitidos').
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/authService';
import { esRolDePortal, inicioDePortal, rutaPermitidaEnPortal } from '../utils/portales';

/**
 * ProtectedRoute
 * @param {ReactNode} children - Componente a renderizar si tiene acceso
 * @param {string[]} rolesPermitidos - (Opcional) Lista de roles que pueden acceder.
 *                                     Si se omite, cualquier usuario autenticado puede acceder.
 * Ejemplo de uso con rol:
 *   <ProtectedRoute rolesPermitidos={['admin']}>
 *     <GestionUsuarios />
 *   </ProtectedRoute>
 */
const ProtectedRoute = ({ children, rolesPermitidos }) => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const location = useLocation();
  const [crossTenantDetected, setCrossTenantDetected] = useState(false);

  // Validar sesión cruzada entre tenants al montar
  useEffect(() => {
    if (!loading && isAuthenticated) {
      // Verificar si hay sesión cruzada (token de otro host/tenant)
      if (authService.hasCrossTenantSession()) {
        authService.registerCrossTenantWarning('protected-route-guard');
        setCrossTenantDetected(true);
        // Limpiar sesión cruzada automáticamente
        logout();
      }
    }
  }, [loading, isAuthenticated, logout]);

  // Mientras carga la autenticación inicial, no redirigir
  if (loading) return null;

  // Si se detectó sesión cruzada, redirigir al login
  if (crossTenantDetected) {
    return <Navigate to="/login" replace />;
  }

  // Si no está autenticado, redirigir al login recordando a dónde iba.
  //
  // Sin el `state`, quien entra por un enlace directo —o abre la app instalada,
  // que arranca en /app— se logueaba y aterrizaba en el dashboard de escritorio,
  // teniendo que volver a navegar a mano hasta donde quería ir. En un celular,
  // parado al lado de una máquina, eso es un viaje de ida.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Roles con portal propio (mecánico, despachante): no salen de su portal, ni
  // tipeando la URL. Se aplica acá y no ruta por ruta para que una pantalla
  // nueva no quede abierta por olvido. La traba real es del backend
  // (PortalScopeMiddleware); esto solo evita el 403 en una pantalla vacía.
  const rolUsuario = user?.rol;
  if (esRolDePortal(rolUsuario) && !rutaPermitidaEnPortal(rolUsuario, location.pathname)) {
    return <Navigate to={inicioDePortal(rolUsuario)} replace />;
  }

  // Si se especificaron roles y el usuario no tiene el rol adecuado → sin acceso
  if (rolesPermitidos && rolesPermitidos.length > 0) {
    if (!rolesPermitidos.includes(rolUsuario)) {
      // Redirigir al inicio con mensaje implícito (sin acceso)
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
